import React, { useState } from "react";
import Icon from "components/AppIcon";

const NEIGHBORHOODS = [
  { id: 1, name: "Dundee", appreciation: 7.8, velocity: 92, medianPrice: 385000, dom: 16, inventory: 28, trend: "up" },
  { id: 2, name: "Aksarben", appreciation: 7.1, velocity: 88, medianPrice: 342000, dom: 19, inventory: 35, trend: "up" },
  { id: 3, name: "Midtown Crossing", appreciation: 6.5, velocity: 84, medianPrice: 298000, dom: 22, inventory: 42, trend: "up" },
  { id: 4, name: "Benson", appreciation: 6.2, velocity: 80, medianPrice: 265000, dom: 24, inventory: 48, trend: "up" },
  { id: 5, name: "West Omaha", appreciation: 5.9, velocity: 76, medianPrice: 412000, dom: 26, inventory: 62, trend: "up" },
  { id: 6, name: "Papillion", appreciation: 4.8, velocity: 70, medianPrice: 355000, dom: 31, inventory: 74, trend: "neutral" },
  { id: 7, name: "Bellevue", appreciation: 4.1, velocity: 63, medianPrice: 248000, dom: 38, inventory: 88, trend: "neutral" },
  { id: 8, name: "Council Bluffs", appreciation: 2.8, velocity: 54, medianPrice: 198000, dom: 46, inventory: 112, trend: "down" },
];

const SORT_KEYS = ["appreciation", "velocity", "medianPrice", "dom"];

const formatPrice = (v) => {
  if (v >= 1000000) return `$${(v / 1000000)?.toFixed(2)}M`;
  return `$${(v / 1000)?.toFixed(0)}K`;
};

export default function NeighborhoodPerformanceTable() {
  const [sortKey, setSortKey] = useState("appreciation");
  const [sortDir, setSortDir] = useState("desc");

  const sorted = [...NEIGHBORHOODS]?.sort((a, b) => {
    const dir = sortDir === "desc" ? -1 : 1;
    return (a?.[sortKey] - b?.[sortKey]) * dir;
  });

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "desc" ? "asc" : "desc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  const getTrendIcon = (trend) => {
    if (trend === "up") return { name: "TrendingUp", color: "var(--color-success)" };
    if (trend === "down") return { name: "TrendingDown", color: "var(--color-accent)" };
    return { name: "Minus", color: "var(--color-warning)" };
  };

  const getVelocityBar = (v) => {
    const pct = Math.round(v);
    const color = pct >= 80 ? "bg-emerald-500" : pct >= 60 ? "bg-blue-500" : "bg-amber-400";
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
        </div>
        <span className="text-xs font-mono-data w-6 text-right">{pct}</span>
      </div>
    );
  };

  const SortBtn = ({ k, label }) => (
    <button
      onClick={() => handleSort(k)}
      className="flex items-center gap-1 text-xs font-caption font-medium text-muted-foreground hover:text-foreground transition-standard"
    >
      {label}
      <Icon
        name={sortKey === k ? (sortDir === "desc" ? "ChevronDown" : "ChevronUp") : "ChevronsUpDown"}
        size={12}
        color="currentColor"
      />
    </button>
  );

  return (
    <div className="card-base flex flex-col gap-4 h-full">
      <div>
        <h3 className="font-heading font-semibold text-base text-foreground">Neighborhood Performance</h3>
        <p className="text-xs font-caption text-muted-foreground mt-0.5">Ranked by appreciation & sales velocity</p>
      </div>
      <div className="overflow-x-auto -mx-1">
        <table className="w-full min-w-[420px] text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left pb-2 pl-1 text-xs font-caption font-medium text-muted-foreground w-6">#</th>
              <th className="text-left pb-2 text-xs font-caption font-medium text-muted-foreground">Area</th>
              <th className="text-right pb-2 pr-1"><SortBtn k="appreciation" label="Appr." /></th>
              <th className="text-right pb-2 pr-1"><SortBtn k="medianPrice" label="Median" /></th>
              <th className="text-right pb-2 pr-1"><SortBtn k="dom" label="DOM" /></th>
              <th className="pb-2 pl-2 w-24"><SortBtn k="velocity" label="Velocity" /></th>
            </tr>
          </thead>
          <tbody>
            {sorted?.map((n, i) => {
              const trendIcon = getTrendIcon(n?.trend);
              return (
                <tr key={n?.id} className="border-b border-border/50 hover:bg-muted/40 transition-standard">
                  <td className="py-2.5 pl-1 text-xs font-caption text-muted-foreground">{i + 1}</td>
                  <td className="py-2.5 pr-2">
                    <div className="flex items-center gap-1.5">
                      <Icon name={trendIcon?.name} size={12} color={trendIcon?.color} />
                      <span className="text-xs font-caption font-medium text-foreground line-clamp-1">{n?.name}</span>
                    </div>
                  </td>
                  <td className="py-2.5 pr-1 text-right">
                    <span className={`text-xs font-mono-data font-semibold ${n?.appreciation >= 6 ? "text-emerald-600" : n?.appreciation >= 4 ? "text-blue-600" : "text-amber-500"}`}>
                      +{n?.appreciation}%
                    </span>
                  </td>
                  <td className="py-2.5 pr-1 text-right">
                    <span className="text-xs font-mono-data text-foreground">{formatPrice(n?.medianPrice)}</span>
                  </td>
                  <td className="py-2.5 pr-1 text-right">
                    <span className="text-xs font-mono-data text-muted-foreground">{n?.dom}d</span>
                  </td>
                  <td className="py-2.5 pl-2">{getVelocityBar(n?.velocity)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-4 pt-1 border-t border-border">
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          <span className="text-xs font-caption text-muted-foreground">Hot ≥80</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-blue-500" />
          <span className="text-xs font-caption text-muted-foreground">Active 60–79</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-amber-400" />
          <span className="text-xs font-caption text-muted-foreground">Slow &lt;60</span>
        </div>
      </div>
    </div>
  );
}